import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, FormControl, InputLabel, Select, MenuItem, Tooltip,
  CircularProgress, Alert,
} from '@mui/material';
import {
  FactCheckOutlined as AuditIcon, FileDownloadOutlined as ExportIcon,
  RestartAltOutlined as ResetIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const EMPTY_FILTERS = {
  start_date: '', end_date: '', action: '', outcome: '', resource_type: '', q: '',
};

function outcomeColor(outcome) {
  const o = String(outcome || '').toLowerCase();
  if (o === 'success') return 'success';
  if (o === 'failure' || o === 'error' || o === 'denied') return 'error';
  return 'default';
}

function fmtTime(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' });
}

export default function AuditLog() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [data, setData] = useState({ entries: [], total: 0, facets: { actions: [], outcomes: [], resource_types: [] } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const buildParams = useCallback((f) => {
    const p = {};
    Object.entries(f).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/v1/superadmin/audit-log`, {
        params: { ...buildParams(applied), limit: rowsPerPage, offset: page * rowsPerPage },
      });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load the audit trail.');
    } finally {
      setLoading(false);
    }
  }, [applied, page, rowsPerPage, buildParams]);

  useEffect(() => { load(); }, [load]);

  const applyFilters = () => { setPage(0); setApplied(filters); };
  const resetFilters = () => { setFilters(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); setPage(0); };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/api/v1/superadmin/audit-log/export`, {
        params: buildParams(applied), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `scribelive-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const facets = data.facets || { actions: [], outcomes: [], resource_types: [] };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AuditIcon sx={{ color: '#c9a227', fontSize: 32 }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>Audit Trail</Typography>
            <Typography variant="body2" color="text.secondary">
              HIPAA access log — who accessed or changed what, when, from where, and the outcome.
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined" startIcon={exporting ? <CircularProgress size={16} /> : <ExportIcon />}
          onClick={handleExport} disabled={exporting || data.total === 0}
        >
          Export CSV
        </Button>
      </Box>

      <Card elevation={0} sx={{ border: '1px solid rgba(15,23,42,0.08)', borderRadius: 3, mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={6} sm={3} md={2}>
              <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
                value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
                value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select label="Action" value={filters.action}
                  onChange={(e) => setFilters({ ...filters, action: e.target.value })}>
                  <MenuItem value="">All</MenuItem>
                  {facets.actions.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Outcome</InputLabel>
                <Select label="Outcome" value={filters.outcome}
                  onChange={(e) => setFilters({ ...filters, outcome: e.target.value })}>
                  <MenuItem value="">All</MenuItem>
                  {facets.outcomes.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField fullWidth size="small" label="Search" placeholder="email, IP, resource id"
                value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }} />
            </Grid>
            <Grid item xs={12} sm={6} md={2} sx={{ display: 'flex', gap: 1 }}>
              <Button fullWidth variant="contained" onClick={applyFilters}
                sx={{ bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' } }}>Apply</Button>
              <Tooltip title="Reset filters">
                <Button variant="outlined" onClick={resetFilters} sx={{ minWidth: 0, px: 1.5 }}><ResetIcon /></Button>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card elevation={0} sx={{ border: '1px solid rgba(15,23,42,0.08)', borderRadius: 3 }}>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['Time (local)', 'User', 'Action', 'Resource', 'Outcome', 'IP', 'Details'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}><CircularProgress /></TableCell></TableRow>
              ) : data.entries.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  No audit entries match these filters.
                </TableCell></TableRow>
              ) : (
                data.entries.map((row) => {
                  const detailsStr = row.details && Object.keys(row.details).length ? JSON.stringify(row.details) : '';
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTime(row.timestamp)}</TableCell>
                      <TableCell>
                        {row.user_email ? (
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{row.user_email}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.user_name || ''}{row.user_role ? ` · ${row.user_role}` : ''}
                            </Typography>
                          </Box>
                        ) : <Typography variant="body2" color="text.secondary">system / anonymous</Typography>}
                      </TableCell>
                      <TableCell><Typography variant="body2" fontFamily="monospace">{row.action}</Typography></TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.resource_type || '—'}</Typography>
                        {row.resource_id && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {row.resource_id}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell><Chip size="small" label={row.outcome || '—'} color={outcomeColor(row.outcome)}
                        variant={outcomeColor(row.outcome) === 'default' ? 'outlined' : 'filled'} /></TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.ip_address || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>
                        {detailsStr ? (
                          <Tooltip title={detailsStr}>
                            <Typography variant="caption" sx={{
                              fontFamily: 'monospace', display: 'block', overflow: 'hidden',
                              textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'text.secondary',
                            }}>{detailsStr}</Typography>
                          </Tooltip>
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data.total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Card>
    </Box>
  );
}
